import { Dispatch } from "redux";
import { ThunkAction } from "redux-thunk";

import { RequestStatus } from "types/requests";
import { IRootStateProps } from "types/reducers";
import {
  getEnforcementSettings,
  IEnforcementSettings,
  postEnforcementSettings,
} from "interfaces/settings";

type SettingsActionTypes =
  | ISetEnforcementRequestStatusAction
  | IReceiveEnforcementAction
  | ISetEnforcementPostStatus
  | IUpdateEnforcementSettings;

type SettingsThunkResult<R> = ThunkAction<
  R,
  IRootStateProps,
  undefined,
  SettingsActionTypes
>;

enum SettingsTypeKeys {
  REQUEST_ENFORCEMENT = "SETTINGS/REQUEST_ENFORCEMENT",
  RECEIVE_ENFORCEMENT = "SETTINGS/RECEIVE_ENFORCEMENT",
  SET_ENFORCEMENT_SETTINGS_REQUEST_STATUS = "SETTINGS/SET_ENFORCEMENT_SETTINGS_REQUEST_STATUS",
  SET_ENFORCEMENT_POST_STATUS = "SETTINGS/SET_ENFORCEMENT_POST_STATUS",
  UPDATE_ENFORCEMENT_SETTINGS = "SETTINGS/UPDATE_ENFORCEMENT_SETTINGS",
}

interface IReceiveEnforcementAction {
  type: SettingsTypeKeys.RECEIVE_ENFORCEMENT;
  enforcementSettings: IEnforcementSettings;
}

function receiveEnforcementSetting(
  enforcementSettings: IEnforcementSettings
): IReceiveEnforcementAction {
  return {
    type: SettingsTypeKeys.RECEIVE_ENFORCEMENT,
    enforcementSettings,
  };
}

interface ISetEnforcementRequestStatusAction {
  type: SettingsTypeKeys.SET_ENFORCEMENT_SETTINGS_REQUEST_STATUS;
  enforcementRequestStatus: RequestStatus;
}

function setEnforcementSettingsRequestStatus(
  status: RequestStatus
): ISetEnforcementRequestStatusAction {
  return {
    type: SettingsTypeKeys.SET_ENFORCEMENT_SETTINGS_REQUEST_STATUS,
    enforcementRequestStatus: status,
  };
}

function fetchEnforcementSettings(
  permalink: string
): SettingsThunkResult<void> {
  return (dispatch: Dispatch): Promise<void> => {
    dispatch(setEnforcementSettingsRequestStatus(RequestStatus.REQUESTING));

    return getEnforcementSettings(permalink)
      .then((enforcementSettings: IEnforcementSettings) => {
        dispatch(
          receiveEnforcementSetting(enforcementSettings as IEnforcementSettings)
        );
        dispatch(setEnforcementSettingsRequestStatus(RequestStatus.SUCCESS));
      })
      .catch(() => {
        dispatch(setEnforcementSettingsRequestStatus(RequestStatus.FAILURE));
      });
  };
}

interface ISetEnforcementPostStatus {
  type: SettingsTypeKeys.SET_ENFORCEMENT_POST_STATUS;
  enforcementPostStatus: RequestStatus;
}

function setEnforcementPostStatus(
  status: RequestStatus
): ISetEnforcementPostStatus {
  return {
    type: SettingsTypeKeys.SET_ENFORCEMENT_POST_STATUS,
    enforcementPostStatus: status,
  };
}

function setEnforcementSettings(
  permalink: string,
  enforcementOn: boolean,
  gracePeriod: number
): SettingsThunkResult<void> {
  return (dispatch: Dispatch): Promise<void> => {
    dispatch(setEnforcementPostStatus(RequestStatus.REQUESTING));

    return postEnforcementSettings(permalink, enforcementOn, gracePeriod)
      .then((enforcementSettings: IEnforcementSettings) => {
        dispatch(setEnforcementPostStatus(RequestStatus.SUCCESS));
        dispatch(updateEnforcementSettings(enforcementSettings));
      })
      .catch(() => {
        dispatch(setEnforcementPostStatus(RequestStatus.FAILURE));
      });
  };
}

interface IUpdateEnforcementSettings {
  type: SettingsTypeKeys.UPDATE_ENFORCEMENT_SETTINGS;
  enforcementOn: boolean;
  gracePeriod: number;
}

function updateEnforcementSettings(
  enforcementSettings: IEnforcementSettings
): IUpdateEnforcementSettings {
  return {
    type: SettingsTypeKeys.UPDATE_ENFORCEMENT_SETTINGS,
    ...enforcementSettings,
  };
}

export {
  fetchEnforcementSettings,
  SettingsTypeKeys,
  SettingsActionTypes,
  SettingsThunkResult,
  setEnforcementSettings,
  updateEnforcementSettings,
  IUpdateEnforcementSettings,
};
